import { motion } from "framer-motion";
import { formatCurrency } from "@/lib/format";

const PALETTE = ["#2D6A4F", "#2A6F7F", "#D99B26", "#D96B43", "#8A9A5B", "#6B7A99", "#B5651D", "#7A6C5D"];

export const IncomePaycheck = ({ result, currency }) => {
  const breakdown = (result.incomeBreakdown || []).filter((b) => b.monthly > 0);
  const totalMonthly = result.firstYearMonthlyIncome || breakdown.reduce((s, b) => s + b.monthly, 0);
  const expenseMonthly = result.firstYearMonthlyExpense || 0;
  const diff = totalMonthly - expenseMonthly;
  const covered = diff >= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl bg-[#1E2B25] text-white p-6 sm:p-7 shadow-lg overflow-hidden relative"
      data-testid="income-paycheck"
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#D99B26] font-semibold mb-1">Your monthly retirement paycheck</p>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-4xl sm:text-5xl font-extrabold" data-testid="paycheck-total">
              {formatCurrency(totalMonthly, currency)}
            </span>
            <span className="text-white/50 text-sm">/ month at age {result.projection?.[0]?.age ?? ""}</span>
          </div>
        </div>
        <div className={`rounded-xl px-4 py-2.5 ${covered ? "bg-[#2D6A4F]/30 border border-[#4C9A6E]/40" : "bg-[#B23A3A]/25 border border-[#E07A7A]/40"}`}
          data-testid="paycheck-gap">
          <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">{covered ? "Monthly surplus" : "Monthly shortfall"}</p>
          <p className={`font-heading text-xl font-bold ${covered ? "text-[#8FE0B0]" : "text-[#F2B8B8]"}`}>
            {covered ? "+" : "−"}{formatCurrency(Math.abs(diff), currency)}
          </p>
        </div>
      </div>

      {/* stacked bar */}
      <div className="flex h-3.5 w-full rounded-full overflow-hidden bg-white/10 mb-4">
        {breakdown.map((b, i) => (
          <div key={b.key || i} style={{ width: `${(b.monthly / (totalMonthly || 1)) * 100}%`, backgroundColor: PALETTE[i % PALETTE.length] }}
            title={`${b.label}: ${formatCurrency(b.monthly, currency)}`} />
        ))}
      </div>

      {/* legend */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="paycheck-breakdown">
        {breakdown.map((b, i) => (
          <div key={b.key || i} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-white/80">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
              {b.label}
            </span>
            <span className="font-mono font-semibold">
              {formatCurrency(b.monthly, currency)}
              <span className="text-white/40 ml-2">{Math.round((b.monthly / (totalMonthly || 1)) * 100)}%</span>
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-sm">
        <span className="text-white/60">Monthly expenses in year 1</span>
        <span className="font-mono font-semibold text-white">{formatCurrency(expenseMonthly, currency)}</span>
      </div>
    </motion.div>
  );
};
